import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface Package {
  id: string
  name: string
  slug: string
  description: string | null
  price_monthly: number
  price_yearly: number
  is_free: boolean
  is_active: boolean
  is_default: boolean
  sort_order: number
  notifications_per_month: number
  live_sessions_per_month: number
  api_calls_per_month: number
  max_applications: number
  max_team_members: number
  max_api_keys: number
  features: string[]
  stripe_price_id_monthly: string | null
  stripe_price_id_yearly: string | null
  created_at: string
  updated_at: string
}

export interface TenantSubscription {
  id: string
  tenant_id: string
  package_id: string
  status: 'active' | 'canceled' | 'past_due' | 'trialing'
  billing_cycle: 'monthly' | 'yearly'
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  created_at: string
  updated_at: string
}

interface CurrentSubscriptionResponse {
  subscription: TenantSubscription | null
  package: Package | null
}

interface UpgradeResponse {
  message?: string
  package: Package
  requires_payment?: boolean
  billing_cycle?: 'monthly' | 'yearly'
  stripe_price_id?: string
}

// Get all available packages
export function usePackages() {
  return useQuery({
    queryKey: ['packages'],
    queryFn: () => api.get<Package[]>('/packages'),
  })
}

// Get current subscription
export function useCurrentSubscription() {
  return useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => api.get<CurrentSubscriptionResponse>('/packages/subscription/current'),
  })
}

// Upgrade to a package
export function useUpgradePackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { package_id: string; billing_cycle?: 'monthly' | 'yearly' }) => {
      return api.post<UpgradeResponse>('/packages/subscription/upgrade', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
  })
}

// Format limit for display
export function formatLimit(value: number): string {
  if (value === -1) return 'Unlimited'
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
  return value.toString()
}

// Format price for display
export function formatPrice(price: number): string {
  if (price === 0) return 'Free'
  return `$${(price / 100).toFixed(2)}`
}
